import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChildren,
} from '@angular/core';

import type { HttpsUrl } from '@kernel';

import { I18nPipe } from '@core/i18n';
import { type MenuItem, NavMode, NavRouteService, NavStore } from '@core/nav';

import { BadgeComponent } from '@shared/badge';
import { ButtonComponent, IconButtonComponent } from '@shared/button';
import { MenuDirective, MenuItemDirective, MenuTriggerDirective } from '@shared/menu';
import { TooltipDirective } from '@shared/tooltip';

@Component({
  selector: 'ks-sidebar',
  imports: [
    ButtonComponent,
    IconButtonComponent,
    I18nPipe,
    BadgeComponent,
    TooltipDirective,
    MenuTriggerDirective,
    MenuDirective,
    MenuItemDirective,
  ],
  host: { class: 'relative flex flex-col min-h-0' },
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent {
  readonly menuExpanded = input<boolean>(true);
  readonly companyLogoUrl = input<HttpsUrl | null>(null);

  readonly menuToggled = output<void>();

  private readonly triggers = viewChildren(MenuTriggerDirective);
  private readonly navStore = inject(NavStore);
  private readonly navRouteService = inject(NavRouteService);

  protected readonly menuItems = this.navStore.menuItems;
  protected readonly currentYear = new Date().getFullYear();
  protected readonly selectedItem = signal<MenuItem | null>(null);

  protected readonly opened = computed(() => this.triggers().some((t) => t.opened()));

  constructor() {
    effect(() => {
      if (this.opened()) return;
      if (!untracked(() => this.selectedItem())) return;

      this.selectedItem.set(null);
    });
  }

  protected selectItem(item: MenuItem): void {
    this.selectedItem.set(item.children?.length ? item : null);

    if (item.path) {
      this.navStore.openTab(item.path, NavMode.Tab);

      return;
    }

    if (item.url) {
      this.navRouteService.openExternalLink(item.url);
    }
  }
}
